import { useEffect, Suspense, lazy, useState } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import { useTranslation } from 'react-i18next'
import changelog from './config/changelog.json'

// Layout
import { DashboardShell } from './components/layout/DashboardShell'
import { CommandPalette } from './components/layout/CommandPalette'
import ErrorBoundary from './components/shared/ErrorBoundary.jsx'

// Hooks
import { useNotifications } from './hooks/useNotifications'
import { useDashboardData } from './hooks/useDashboardData'

import { useOTAUpdate } from './hooks/useOTAUpdate.js'

// Pages
// [LANDING-UNIFY] legacy pages/LandingPage удалён: вторая версия лендинга со старыми ценами (config/plans.js) больше не отдаётся
import PublicLandingPage from './pages/landing/LandingPage'
// [HOTFIX-FINAL-2 З7.2] route-level lazy: всё, кроме первого экрана (лендинг), грузится по требованию.
// Цель: initial bundle <1МБ (было 2.3МБ index — админка/студия/дашборды тянулись на каждый роут)
const SignupPage = lazy(() => import('./pages/auth/SignupPage'))
const ApiDocsPage = lazy(() => import('./pages/docs/ApiDocsPage'))
const OwnerDashboardPage = lazy(() => import('./pages/owner/OwnerDashboardPage'))
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'))
const StaffDashboardPage = lazy(() => import('./pages/StaffDashboardPage'))
const AdvertiserDashboardPage = lazy(() => import('./pages/AdvertiserDashboardPage'))
const CreatorDashboardPage = lazy(() => import('./pages/CreatorDashboardPage'))
const AIVideoCreator = lazy(() => import('./components/video/AIVideoCreator.jsx'))
const NeuroSalesDashboard = lazy(() => import('./components/analytics/NeuroSalesDashboard.jsx'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))
const SchedulerPage = lazy(() => import('./pages/SchedulerPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const VkCallbackPage = lazy(() => import('./pages/VkCallbackPage'))
const DownloadPage = lazy(() => import('./pages/DownloadPage'))
const AIvsHumanPage = lazy(() => import('./pages/owner/AIvsHumanPage'))
const BoardroomPage = lazy(() => import('./pages/owner/BoardroomPage'))
const BusinessSpawnerPage = lazy(() => import('./pages/owner/BusinessSpawnerPage'))
const SupremeStatusPage = lazy(() => import('./pages/omega-supreme/SupremeStatusPage.jsx'))
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'))
const ChallengePage = lazy(() => import('./pages/ChallengePage'))
const AdvertiserRequestsPage = lazy(() => import('./pages/AdvertiserRequestsPage'))
const OwnerAppPage = lazy(() => import('./pages/owner-app/index'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const CreditsPage = lazy(() => import('./pages/CreditsPage'))
const StripeCheckoutPage = lazy(() => import('./pages/StripeCheckoutPage'))
// [CHECKOUT-UNIFY] CheckoutPage удалён: legacy-флоу с ценами не из PlanConfig (redirect на /settings?tab=subscription)
const PrivacyPolicyPage = lazy(() => import('./pages/legal/LegalPage').then(m => ({ default: m.PrivacyPolicyPage })))
const TermsOfServicePage = lazy(() => import('./pages/legal/LegalPage').then(m => ({ default: m.TermsOfServicePage })))
const ConsentPage = lazy(() => import('./pages/legal/LegalPage').then(m => ({ default: m.ConsentPage })))
import { CookieConsent } from './components/CookieConsent'
const GDPRPage = lazy(() => import('./pages/GDPRPage'))
const ProjectFactoryPage = lazy(() => import('./pages/project-factory/ProjectFactoryPage.jsx'))
const PredictionDashboard = lazy(() => import('./pages/prediction/PredictionDashboard.jsx'))
const InvestmentPanel = lazy(() => import('./pages/investment/InvestmentPanel.jsx'))
const BoardroomCommandCenter = lazy(() => import('./pages/boardroom/BoardroomCommandCenter.jsx'))
const LaunchPage = lazy(() => import('./pages/LaunchPage'))
const PublicRoadmap = lazy(() => import('./pages/landing/PublicRoadmap'))
const OnboardingWizard = lazy(() => import('./components/onboarding/OnboardingWizard'))
import UnauthorizedPage from './pages/UnauthorizedPage'
import { UpdateModal } from './components/shared/UpdateModal.jsx'
import { MaintenanceGate } from './components/MaintenanceScreen.jsx'
import toast, { Toaster } from 'react-hot-toast'
import { APP_VERSION } from './config/version.js'
import { API_BASE_URL } from './config.js'

// Version check: warn if backend requires newer frontend
function VersionCheck() {
    const { t } = useTranslation()
    const [update, setUpdate] = useState(null)
    // [OWNER-OMEGA] changelog из БД (редактор владельца); при пустой БД — встроенный changelog.json
    const [liveChangelog, setLiveChangelog] = useState(null)

    useEffect(() => {
        let cancelled = false
        fetch(`${API_BASE_URL}/version/structured-changelog`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data || cancelled) return
                if (Array.isArray(data.entries) && data.entries.length) setLiveChangelog(data.entries)
            })
            .catch(() => {})
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        let cancelled = false

        // [UPDATE-MODAL-SMART] после reload — один раз показываем подтверждение обновления
        try {
            const pendingVersion = localStorage.getItem('pending_update_toast_version')
            if (pendingVersion && pendingVersion === APP_VERSION) {
                toast.success(t('update.toast', { version: APP_VERSION }), { duration: 4000 })
                localStorage.removeItem('pending_update_toast_version')
            }
        } catch {}

        fetch(`${API_BASE_URL}/version`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data || cancelled) return
                // [CLIENT-JOURNEY-QA] показываем модалку только когда фронт СТАРЕЕ требуемого
                // (semver). Раньше любое !== давало вечную модалку «Доступно обновление»:
                // фронт 9.9.21 новее backend 9.9.19 — обновлять нечего, reload не помогает.
                const newer = (a, b) => {
                    const pa = String(a).split('.').map(Number)
                    const pb = String(b).split('.').map(Number)
                    for (let i = 0; i < 3; i++) {
                        if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) > (pb[i] || 0)
                    }
                    return false
                }
                if (data.requiredFrontend && newer(data.requiredFrontend, APP_VERSION)) {
                    setUpdate(data.requiredFrontend)
                }
            })
            .catch(() => {})
        return () => { cancelled = true }
    }, [t])

    if (!update) return null

    // [v9.9.19.2-UX-HOTFIX-v4] PWA update = очистка кэшей + reload, без скачивания пакета.
    // Жёсткий таймаут 3 сек: даже если caches API зависнет, reload всё равно случится.
    const handleUpdate = () => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('jwt')
        if (token) localStorage.setItem('pending_auth_token', token)
        localStorage.setItem('pending_update_toast_version', update)
        const clearCaches = ('caches' in window)
            ? caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k)))).catch(() => {})
            : Promise.resolve()
        Promise.race([clearCaches, new Promise(r => setTimeout(r, 3000))])
            .finally(() => window.location.reload(true))
    }

    return (
        <UpdateModal
            version={update}
            changelog={liveChangelog || changelog}
            onUpdate={handleUpdate}
            onRemind={() => setUpdate(null)}
            onSkip={() => setUpdate(null)}
        />
    )
}

// [v6.0] added: Creative Hub + Luxury Document Viewer
// [HOTFIX-FINAL-2 З7.2] оба — route-level lazy (тяжёлые, не нужны на первом экране)
const CreativeHub = lazy(() => import('./components/creative-hub/CreativeHub.jsx'))
const LuxuryDocumentViewer = lazy(() => import('./components/documents/LuxuryDocumentViewer.jsx'))

// [v6.0] added: simple document viewer wrapper for /documents/:fileId route
// [UI-VERIFY] AIVideoCreator — модальный компонент: на роуте /video-creator его X мёртвый
// без onClose; даём возврат на /dashboard.
function AIVideoCreatorRoute() {
    const navigate = useNavigate()
    return <AIVideoCreator onClose={() => navigate('/dashboard')} />
}

function DocumentPage() {
    const { fileId } = useParams()
    const navigate = useNavigate()
    const [content, setContent] = useState('')
    const [fileName, setFileName] = useState(fileId || 'document')
    const [fileType, setFileType] = useState('txt')
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const ext = (fileName.split('.').pop() || 'txt').toLowerCase()
        setFileType(ext)
        fetch(`${API_BASE_URL}/documents/${fileId}`)
            .then(async res => {
                if (!res.ok) throw new Error('Document not found')
                const text = await res.text()
                setContent(text)
            })
            .catch(() => {
                setContent('// Document preview is not available for this file.\n// [v6.0] added placeholder')
            })
            .finally(() => setLoading(false))
    }, [fileId, fileName])

    if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading document…</div>

    return (
        <div className="min-h-screen bg-[var(--bg)] p-4">
            <LuxuryDocumentViewer
                content={content}
                fileName={fileName}
                fileType={fileType}
                onClose={() => navigate(-1)}
                onDownload={() => toast('Download: ' + fileName)}
            />
        </div>
    )
}

const PAGE_TITLES = {
    '/owner': 'Owner Dashboard',
    '/admin': 'Admin Panel',
    '/staff': 'Staff Panel',
    '/advertiser': 'Advertiser Panel',
    '/dashboard': 'Creator Dashboard',
    '/ai-chat': 'AI Chat',
    '/analytics': 'Analytics',
    '/scheduler': 'Scheduler',
    '/settings': 'Settings',
    '/analyzer': 'Content Analyzer',
    '/viral-chat': 'Viral Chat',
    '/ai-vs-human': 'AI vs Human',
    '/boardroom': 'AI Boardroom',
    '/business-spawner': 'Business Spawner',
    '/advertiser-requests': 'Заявки на рекламу',
}

// ============================================
// PROTECTED ROUTE
// ============================================
function ProtectedRoute({ children, allowedRoles }) {
    const { user, isAuthenticated, loading, authError, roleSwitching, logout } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()

    const { notifications, unreadCount, markRead, markAllRead, remove } = useNotifications()
    // [VIEW-AS-PERSIST] owner-only эндпоинты (/owner/team, /owner/subscriptions) дёргаем только
    // когда эффективная роль — owner. В view-as режиме (realRole=owner, role=staff/…) — не шлём,
    // консоль чистая от 401 (образец — role-guard в OmegaChat)
    const canOwnerFetch = user?.role === 'owner'
    const { data: teamData } = useDashboardData('team', { immediate: canOwnerFetch })
    const { data: subsData } = useDashboardData('subscriptions', { immediate: canOwnerFetch })

    const handleLogout = () => {
        logout()
        navigate('/')
    }

    // [ROLE-SWITCH-FLASH] roleSwitching: идёт refresh профиля после смены роли —
    // показываем спиннер; /unauthorized — только при подтверждённой чужой роли
    if (loading || roleSwitching) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-[#00ff41] border-t-transparent rounded-full" />
            </div>
        )
    }

    if (!isAuthenticated) {
        // [HOTFIX-FINAL-2 З2] токен жив, но сервер недоступен и кэша нет — error-state с retry
        // вместо вечного спиннера/молчаливого редиректа (TG WebView, спящий backend)
        if (authError) {
            return (
                <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center gap-4 p-6 text-center text-gray-200">
                    <div className="text-3xl">⚠️</div>
                    <div className="text-lg font-semibold">Нет соединения с сервером</div>
                    <p className="text-sm text-gray-400 max-w-xs">Проверьте интернет и попробуйте ещё раз.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 min-h-[44px] rounded-2xl bg-[#00ff41] text-black font-semibold"
                    >
                        Повторить
                    </button>
                </div>
            )
        }
        return <Navigate to="/" replace />
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
        return <Navigate to="/unauthorized" replace />
    }

    return (
        <DashboardShell
            userRole={user?.role || 'creator'}
            user={user}
            title={PAGE_TITLES[location.pathname] || 'AI Viral Studio'}
            cabinets={teamData?.cabinets || []}
            subscriptions={subsData?.subscriptions || []}
            notifications={notifications}
            unreadCount={unreadCount}
            onLogout={handleLogout}
            onMarkNotificationRead={markRead}
            onMarkAllNotificationsRead={markAllRead}
            onDeleteNotification={remove}
        >
            {children}
        </DashboardShell>
    )
}

// ============================================
// ROLE REDIRECT
// ============================================
function RoleRedirect() {
    const { user } = useAuth()

    switch (user?.role) {
        case 'owner': return <Navigate to="/owner" replace />
        case 'admin': return <Navigate to="/admin" replace />
        case 'staff': return <Navigate to="/staff" replace />
        case 'advertiser': return <Navigate to="/advertiser" replace />
        default: return <Navigate to="/dashboard" replace />
    }
}

// ============================================
// APP
// ============================================
function App() {
  const BUILD_ID = 'v9.9.21-2026-08-14'; console.log('[BUILD]', BUILD_ID);
      // v9.9.21-ui-polish
  window.__APP_BUILD__ = 'v9.9.21';
  console.log('[AI VIRAL STUDIO] Build:', window.__APP_BUILD__);
  useOTAUpdate()
  useEffect(() => {
        const meta = document.createElement('meta')
        meta.name = 'viewport'
        meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes, viewport-fit=cover'
        document.head.appendChild(meta)

        const metaTheme = document.createElement('meta')
        metaTheme.name = 'theme-color'
        metaTheme.content = '#0a0a0f'
        document.head.appendChild(metaTheme)

        return () => {
            document.head.removeChild(meta)
            document.head.removeChild(metaTheme)
        }
    }, [])

    return (
        <>
            <Toaster position="top-right" toastOptions={{ style: { background: '#1a1a2e', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' } }} />
            <MaintenanceGate />
            <VersionCheck />
            <Suspense fallback={
                <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                    <div className="animate-spin w-8 h-8 border-2 border-[#00ff41] border-t-transparent rounded-full" />
                </div>
            }>
                <ErrorBoundary>
                <Routes>
                <Route path="/" element={<PublicLandingPage />} />
                {/* [LANDING-UNIFY] /login и /register — тот же единый лендинг с модалкой входа/регистрации (формы не тронуты) */}
                <Route path="/login" element={<PublicLandingPage authMode="login" />} />
                <Route path="/register" element={<PublicLandingPage authMode="register" />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/docs" element={<ApiDocsPage />} />
                <Route path="/redirect" element={<RoleRedirect />} />

                {/* [DESIGN-LAB-APPLY] лаборатория /preview/* удалена после применения выбранных дизайнов
                    (вариант Б — в кабинете рекламодателя; чат/профиль/студия — в боевых экранах).
                    Клиент на черновики попасть не может: роутов больше нет → общий "*" → редирект. */}

                {/* [v6.0] added: backward compatible redirects to Creative Hub */}
                <Route path="/chat" element={<Navigate to="/creative-hub/chat" replace />} />
                <Route path="/analyzer" element={<Navigate to="/creative-hub/analyzer" replace />} />
                <Route path="/planner" element={<Navigate to="/creative-hub/planner" replace />} />
                <Route path="/viral" element={<Navigate to="/creative-hub/viral" replace />} />
                <Route path="/viral-chat" element={<Navigate to="/creative-hub/viral" replace />} />
                <Route path="/omega-chat" element={<Navigate to="/creative-hub/chat" replace />} />
                <Route path="/ai-chat" element={<Navigate to="/creative-hub/chat" replace />} />

                <Route path="/creative-hub" element={
                    <ProtectedRoute>
                        <CreativeHub />
                    </ProtectedRoute>
                } />
                <Route path="/creative-hub/:mode" element={
                    <ProtectedRoute>
                        <CreativeHub />
                    </ProtectedRoute>
                } />
                <Route path="/documents/:fileId" element={
                    <ProtectedRoute>
                        <DocumentPage />
                    </ProtectedRoute>
                } />

                <Route path="/owner" element={
                    <ProtectedRoute allowedRoles={['owner']}>
                        <OwnerDashboardPage />
                    </ProtectedRoute>
                } />
                <Route path="/admin" element={
                    <ProtectedRoute allowedRoles={['admin', 'owner']}>
                        <AdminDashboardPage />
                    </ProtectedRoute>
                } />
                <Route path="/staff" element={
                    <ProtectedRoute allowedRoles={['staff', 'admin', 'owner']}>
                        <StaffDashboardPage />
                    </ProtectedRoute>
                } />
                <Route path="/advertiser" element={
                    <ProtectedRoute allowedRoles={['advertiser', 'owner']}>
                        <AdvertiserDashboardPage />
                    </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                    <ProtectedRoute allowedRoles={['creator', 'business', 'owner']}>
                        <CreatorDashboardPage />
                    </ProtectedRoute>
                } />
                {/* [CHAT-UNIFY] мёртвый дубль роута /ai-chat удалён — выше (л.~309) уже стоит редирект на /creative-hub/chat */}
                <Route path="/analytics" element={
                    <ProtectedRoute>
                        <AnalyticsPage />
                    </ProtectedRoute>
                } />
                <Route path="/scheduler" element={
                    <ProtectedRoute>
                        <SchedulerPage />
                    </ProtectedRoute>
                } />
                <Route path="/video-creator" element={
                    <ProtectedRoute allowedRoles={['creator', 'pro', 'agency', 'owner', 'admin']}>
                        <AIVideoCreatorRoute />
                    </ProtectedRoute>
                } />
                <Route path="/neuro-sales" element={
                    <ProtectedRoute allowedRoles={['advertiser', 'pro', 'agency', 'owner', 'admin']}>
                        <NeuroSalesDashboard />
                    </ProtectedRoute>
                } />
                <Route path="/omega-supreme" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                        <SupremeStatusPage />
                    </ProtectedRoute>
                } />
                <Route path="/project-factory" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                        <ProjectFactoryPage />
                    </ProtectedRoute>
                } />
                <Route path="/prediction" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                        <PredictionDashboard />
                    </ProtectedRoute>
                } />
                <Route path="/investment" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                        <InvestmentPanel />
                    </ProtectedRoute>
                } />
                <Route path="/settings" element={
                    <ProtectedRoute>
                        <SettingsPage />
                    </ProtectedRoute>
                } />
                {/* [HOTFIX-FINAL] витрина пакетов кредитов — цель всех кнопок «Пополнить» */}
                <Route path="/credits" element={
                    <ProtectedRoute>
                        <CreditsPage />
                    </ProtectedRoute>
                } />
                <Route path="/auth/vk/callback" element={
                    <ProtectedRoute>
                        <VkCallbackPage />
                    </ProtectedRoute>
                } />
                <Route path="/ai-vs-human" element={
                    <ProtectedRoute>
                        <AIvsHumanPage />
                    </ProtectedRoute>
                } />
                <Route path="/boardroom" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin', 'business']}>
                        <BoardroomCommandCenter />
                    </ProtectedRoute>
                } />
                <Route path="/business-spawner" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin', 'business']}>
                        <BusinessSpawnerPage />
                    </ProtectedRoute>
                } />
                <Route path="/leaderboard" element={
                    <ProtectedRoute allowedRoles={['creator', 'business', 'owner']}>
                        <LeaderboardPage />
                    </ProtectedRoute>
                } />
                <Route path="/challenge" element={
                    <ProtectedRoute allowedRoles={['creator', 'business', 'owner']}>
                        <ChallengePage />
                    </ProtectedRoute>
                } />
                <Route path="/advertiser-requests" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                        <AdvertiserRequestsPage />
                    </ProtectedRoute>
                } />

                <Route path="/owner-app" element={
                    <ProtectedRoute allowedRoles={['owner']}>
                        <OwnerAppPage />
                    </ProtectedRoute>
                } />

                <Route path="/developer" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin', 'business']}>
                        <Navigate to="/owner?tab=developer" replace />
                    </ProtectedRoute>
                } />

                <Route path="/white-label" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                        <Navigate to="/owner?tab=whiteLabel" replace />
                    </ProtectedRoute>
                } />

                {/* [CHECKOUT-UNIFY] цель редиректа (/owner?tab=workspaces) owner-only —
                    business попадал на /unauthorized цепочкой; роут нигде не линкуется */}
                <Route path="/workspaces" element={
                    <ProtectedRoute allowedRoles={['owner', 'admin']}>
                        <Navigate to="/owner?tab=workspaces" replace />
                    </ProtectedRoute>
                } />

                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                {/* [CHECKOUT-UNIFY] legacy /checkout (дубль флоу с ценами Creator 2900₽/Pro 7900₽ из in-memory PLANS)
                    заменён редиректом на живой экран тарифов (PlanConfig) в кабинете.
                    Единственная ссылка на него (UpgradeNudge topup) вела на несуществующий план (404). */}
                <Route path="/checkout" element={<Navigate to="/settings?tab=subscription" replace />} />
                <Route path="/stripe-checkout" element={
                    <ProtectedRoute>
                        <StripeCheckoutPage />
                    </ProtectedRoute>
                } />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/consent" element={<ConsentPage />} />
                <Route path="/gdpr" element={
                    <ProtectedRoute>
                        <GDPRPage />
                    </ProtectedRoute>
                } />
                <Route path="/data-export" element={
                    <ProtectedRoute>
                        <Navigate to="/gdpr" replace />
                    </ProtectedRoute>
                } />
                <Route path="/launch" element={<LaunchPage />} />
                <Route path="/roadmap" element={<PublicRoadmap />} />
                <Route path="/download" element={<DownloadPage />} />
                <Route path="/onboarding" element={<OnboardingWizard />} />
                <Route path="/welcome" element={<Navigate to="/onboarding" replace />} />

                {/* [CHECKOUT-UNIFY] дружелюбная «нет доступа» вместо молчаливого лендинга */}
                <Route path="/unauthorized" element={<UnauthorizedPage />} />

                <Route path="/business" element={
                    <ProtectedRoute allowedRoles={['business', 'owner', 'admin']}>
                        <Navigate to="/dashboard" replace />
                    </ProtectedRoute>
                } />

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
                </ErrorBoundary>
            </Suspense>

            <CookieConsent />
        <CommandPalette />

    </>
)
}

export default App




